import React, { useState, useCallback } from 'react';
import Component from '../../components/Component';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SearchIcon from '@mui/icons-material/Search';
import { lookupMatch, regenerateHookQuestions } from '../../services/hookQuestionsAdminApi';

function statusLabel(status) {
    // matches.status: 1=upcoming, 2=completed, 3=live (others vary)
    if (status === 1) return { label: 'Upcoming', color: '#1976d2' };
    if (status === 2) return { label: 'Completed', color: '#7b1fa2' };
    if (status === 3) return { label: 'Live', color: '#d32f2f' };
    return { label: String(status), color: '#616161' };
}

function ScenarioResultRow({ r }) {
    const ok = !r.error && !r.skipped;
    const color = r.error ? '#d32f2f' : r.skipped ? '#ed6c02' : '#2e7d32';
    const status = r.error ? 'ERROR' : r.skipped ? `SKIPPED · ${r.reason || ''}` : 'GENERATED';
    return (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75 }}>
            <Chip label={status} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 600, minWidth: 130, fontSize: '0.7rem' }} />
            <Typography variant="body2" fontFamily="monospace">{r.scenario_id}</Typography>
            {r.question_id && (
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">→ {r.question_id}</Typography>
            )}
            {r.error && (
                <Typography variant="caption" color="error">— {r.error}</Typography>
            )}
        </Stack>
    );
}

function HookQuestionsAdmin() {
    const [matchId, setMatchId] = useState('');
    const [situation, setSituation] = useState('pre_match');
    const [match, setMatch] = useState(null);
    const [lookupErr, setLookupErr] = useState('');
    const [lookupLoading, setLookupLoading] = useState(false);

    const [regenLoading, setRegenLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [result, setResult] = useState(null);

    const doLookup = useCallback(async () => {
        if (!matchId.trim()) { setLookupErr('match_id required'); return; }
        setLookupLoading(true); setLookupErr(''); setMatch(null);
        try {
            const res = await lookupMatch(matchId.trim());
            setMatch(res?.data || null);
        } catch (err) {
            setLookupErr(err.message || 'Lookup failed');
        } finally {
            setLookupLoading(false);
        }
    }, [matchId]);

    const handleRegen = async () => {
        setConfirmOpen(false);
        setRegenLoading(true); setError(''); setInfo(''); setResult(null);
        try {
            const res = await regenerateHookQuestions({ matchId: matchId.trim(), situation });
            const data = res?.data || {};
            setResult(data);
            const sec = ((data.generated_in_ms || 0) / 1000).toFixed(1);
            setInfo(`${data.generated} generated · ${data.skipped} skipped · ${data.errors} errors · ${data.deleted} cleared · ${sec}s`);
        } catch (err) {
            setError(err.message || 'Regeneration failed');
        } finally {
            setRegenLoading(false);
        }
    };

    const matchStatus = match ? statusLabel(match.status) : null;

    return (
        <Component>
            <div className="page-body">
                <div className="container-fluid">
                    <Box sx={{ py: 2 }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" fontWeight={700}>Hook Questions — Manual Generator</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Pick a match, pick pre-match or post-match, and force-regenerate all hook questions for that phase. Old questions for the same (match, phase) are cleared first.
                            </Typography>
                        </Box>

                        {/* Input row */}
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                                    <TextField
                                        label="match_id"
                                        value={matchId}
                                        onChange={(e) => setMatchId(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') doLookup(); }}
                                        size="small"
                                        sx={{ flex: 1, minWidth: 220 }}
                                        placeholder="e.g. 81935"
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={lookupLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                                        onClick={doLookup}
                                        disabled={lookupLoading || regenLoading || !matchId.trim()}
                                    >
                                        Lookup
                                    </Button>
                                    <FormControl>
                                        <RadioGroup
                                            row
                                            value={situation}
                                            onChange={(e) => setSituation(e.target.value)}
                                        >
                                            <FormControlLabel value="pre_match" control={<Radio size="small" />} label="Pre-match" />
                                            <FormControlLabel value="post_match" control={<Radio size="small" />} label="Post-match" />
                                        </RadioGroup>
                                    </FormControl>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={regenLoading ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                                        onClick={() => setConfirmOpen(true)}
                                        disabled={regenLoading || lookupLoading || !matchId.trim()}
                                    >
                                        {regenLoading ? 'Generating…' : 'Generate'}
                                    </Button>
                                </Stack>
                                {lookupErr && <Alert severity="error" sx={{ mt: 1.5 }}>{lookupErr}</Alert>}
                            </CardContent>
                        </Card>

                        {/* Match preview */}
                        {match && (
                            <Card sx={{ mb: 2 }}>
                                <CardContent>
                                    <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Match</Typography>
                                            <Typography variant="subtitle1" fontWeight={700}>{match.short_title || match.title || `#${match.match_id}`}</Typography>
                                            <Typography variant="caption" color="text.secondary">{match.competition || '—'}</Typography>
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Teams</Typography>
                                            <Typography variant="subtitle1" fontWeight={700}>{match.team_a} vs {match.team_b}</Typography>
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Status</Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                                {matchStatus && <Chip label={matchStatus.label} size="small" sx={{ bgcolor: matchStatus.color, color: '#fff', fontWeight: 600 }} />}
                                            </Box>
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Venue · Start (IST)</Typography>
                                            <Typography variant="body2" fontWeight={600}>{match.venue || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{match.date_start_ist || match.date_start || ''}</Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                        {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

                        {/* Per-scenario result */}
                        {result?.results?.length > 0 && (
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                        {result.phase === 'pre_match' ? 'Pre-match' : 'Post-match'} scenarios
                                    </Typography>
                                    {result.results.map((r, i) => <ScenarioResultRow key={r.scenario_id || i} r={r} />)}
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                </div>
            </div>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Generate {situation === 'pre_match' ? 'pre-match' : 'post-match'} hook questions?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will <strong>delete</strong> the existing hook questions for match <code>{matchId}</code> in the <code>{situation}</code> phase and rebuild every scenario from scratch. Each scenario costs ~1 LLM call. Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleRegen} variant="contained" color="primary">Generate</Button>
                </DialogActions>
            </Dialog>
        </Component>
    );
}

export default HookQuestionsAdmin;
